// =============================================
// Delete Confirmation Modal
// KipiKid - אישור מחיקה מעוצב
// =============================================

import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Loader2 } from 'lucide-react';

const colors = {
  error: '#dc2626',
  errorLight: '#fef2f2',
  text: '#1f2937',
  textLight: '#6b7280',
  border: '#e5e7eb',
  background: '#f9fafb',
};

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  itemName: string;
  warningMessage?: string;
  isLoading?: boolean;
}

export default function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'אישור מחיקה',
  itemName,
  warningMessage,
  isLoading = false,
}: DeleteConfirmModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 200,
            padding: '16px',
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 400 }}
            style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '24px',
              width: '100%',
              maxWidth: '380px',
              textAlign: 'center',
            }}
            dir="rtl"
          >
            {/* Warning Icon */}
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: colors.errorLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <AlertTriangle size={28} color={colors.error} />
            </div>

            {/* Title */}
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: colors.text,
              marginBottom: '8px',
            }}>
              {title}
            </h3>

            {/* Message */}
            <p style={{
              fontSize: '14px',
              color: colors.textLight,
              marginBottom: '8px',
              lineHeight: '1.5',
            }}>
              האם למחוק את{' '}
              <strong style={{ color: colors.text }}>{itemName}</strong>?
            </p>

            {/* Warning */}
            {warningMessage && (
              <p style={{
                fontSize: '13px',
                color: colors.error,
                backgroundColor: colors.errorLight,
                padding: '10px 14px',
                borderRadius: '8px',
                marginBottom: '16px',
              }}>
                {warningMessage}
              </p>
            )}

            {!warningMessage && <div style={{ height: '8px' }} />}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <motion.button
                autoFocus
                onClick={onClose}
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: 'white',
                  color: colors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                ביטול
              </motion.button>
              <motion.button
                onClick={onConfirm}
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: colors.error,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
                whileTap={{ scale: isLoading ? 1 : 0.98 }}
              >
                {isLoading && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                {isLoading ? 'מוחק...' : 'מחיקה'}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
